import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Filter, Calendar, Mail, Phone, User, MessageSquare, CheckCircle, Clock, XCircle, ChevronDown, ChevronUp, LogOut } from 'lucide-react';
import { exportToCSV } from '../utils/exportToCSV';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
// Mock data type
type Submission = {
  id: string;
  date: string;
  name: string;
  email: string;
  phone: string;
  type: 'צור קשר' | 'הרשמה לפסטיבל' | 'הצטרפות למועדון';
  message: string;
  status: 'חדש' | 'בטיפול' | 'טופל';
};
// Mock data
const MOCK_DATA: Submission[] = [{
  id: '1',
  date: '2023-10-25 14:30',
  name: 'ישראל ישראלי',
  email: 'israel@example.com',
  phone: '050-1234567',
  type: 'צור קשר',
  message: 'אשמח לקבל פרטים נוספים על שיעורי סאפ למתחילים',
  status: 'חדש'
}, {
  id: '2',
  date: '2023-10-24 09:15',
  name: 'דנה כהן',
  email: 'dana@example.com',
  phone: '052-7654321',
  type: 'הרשמה לפסטיבל',
  message: 'מעוניינת להירשם למקצה העממי',
  status: 'בטיפול'
}, {
  id: '3',
  date: '2023-10-23 16:45',
  name: 'רון לוי',
  email: 'ron@example.com',
  phone: '054-9876543',
  type: 'הצטרפות למועדון',
  message: 'האם יש הנחה לתושבי האזור?',
  status: 'טופל'
}, {
  id: '4',
  date: '2023-10-22 11:20',
  name: 'מיכל אברהם',
  email: 'michal@example.com',
  phone: '053-1122334',
  type: 'צור קשר',
  message: 'רוצה לתאם אירוע חברה לקבוצה של 20 איש',
  status: 'חדש'
}, {
  id: '5',
  date: '2023-10-21 13:10',
  name: 'יוסי מזרחי',
  email: 'yossi@example.com',
  phone: '050-5555555',
  type: 'הרשמה לפסטיבל',
  message: '',
  status: 'טופל'
}];
export function AdminDashboard() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Submission;
    direction: 'ascending' | 'descending';
  } | null>(null);
  // בדיקת הרשאות בטעינת הדף
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('adminAuthenticated');
    if (isAuthenticated !== 'true') {
      navigate('/admin/login');
    }
  }, [navigate]);
  const handleLogout = () => {
    localStorage.removeItem('adminAuthenticated');
    navigate('/admin/login');
  };
  // Filter and sort logic
  const filteredData = useMemo(() => {
    let data = [...MOCK_DATA];
    // Search filter
    if (searchTerm) {
      data = data.filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()) || item.email.toLowerCase().includes(searchTerm.toLowerCase()) || item.phone.includes(searchTerm) || item.type.includes(searchTerm));
    }
    // Status filter
    if (statusFilter !== 'all') {
      data = data.filter(item => item.status === statusFilter);
    }
    // Sorting
    if (sortConfig) {
      data.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return data;
  }, [searchTerm, statusFilter, sortConfig]);
  const requestSort = (key: keyof Submission) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({
      key,
      direction
    });
  };
  const handleExport = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    exportToCSV(filteredData, `submissions_export_${timestamp}.csv`);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'חדש':
        return 'bg-blue-100 text-blue-800';
      case 'בטיפול':
        return 'bg-yellow-100 text-yellow-800';
      case 'טופל':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'חדש':
        return <Clock className="w-4 h-4 ml-1" data-id="element-856" />;
      case 'בטיפול':
        return <Clock className="w-4 h-4 ml-1" data-id="element-857" />;
      case 'טופל':
        return <CheckCircle className="w-4 h-4 ml-1" data-id="element-858" />;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" dir="rtl" data-id="element-859">
      <Navigation data-id="element-860" />

      <main className="flex-grow container mx-auto px-4 py-8 mt-20" data-id="element-861">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-862">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white" data-id="element-863">
            <div data-id="element-864">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-865">ניהול פניות</h1>
              <p className="text-gray-500 text-sm mt-1" data-id="element-866">
                צפייה וניהול של כל הטפסים שנשלחו מהאתר
              </p>
            </div>
            <div className="flex gap-3" data-id="element-867">
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium" data-id="element-868">
                <LogOut className="w-4 h-4" data-id="element-869" />
                התנתק
              </button>
              <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium shadow-sm" data-id="element-870">
                <Download className="w-4 h-4" data-id="element-871" />
                ייצוא לאקסל
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-col md:flex-row gap-4" data-id="element-872">
            <div className="relative flex-grow max-w-md" data-id="element-873">
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-874">
                <Search className="h-5 w-5 text-gray-400" data-id="element-875" />
              </div>
              <input type="text" className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="חיפוש לפי שם, אימייל או טלפון..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-876" />
            </div>

            <div className="flex items-center gap-2" data-id="element-877">
              <Filter className="w-5 h-5 text-gray-500" data-id="element-878" />
              <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-879">
                <option value="all" data-id="element-880">כל הסטטוסים</option>
                <option value="חדש" data-id="element-881">חדש</option>
                <option value="בטיפול" data-id="element-882">בטיפול</option>
                <option value="טופל" data-id="element-883">טופל</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto" data-id="element-884">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-885">
              <thead className="bg-gray-50" data-id="element-886">
                <tr data-id="element-887">
                  {[{
                  key: 'date',
                  label: 'תאריך',
                  icon: Calendar
                }, {
                  key: 'name',
                  label: 'שם מלא',
                  icon: User
                }, {
                  key: 'phone',
                  label: 'טלפון',
                  icon: Phone
                }, {
                  key: 'email',
                  label: 'אימייל',
                  icon: Mail
                }, {
                  key: 'type',
                  label: 'סוג פנייה',
                  icon: MessageSquare
                }, {
                  key: 'status',
                  label: 'סטטוס',
                  icon: CheckCircle
                }, {
                  key: 'message',
                  label: 'הודעה',
                  icon: MessageSquare
                }].map(column => <th key={column.key} scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort(column.key as keyof Submission)} data-id="element-888">
                      <div className="flex items-center gap-1" data-id="element-889">
                        <column.icon className="w-3 h-3" data-id="element-890" />
                        {column.label}
                        {sortConfig?.key === column.key && (sortConfig.direction === 'ascending' ? <ChevronUp className="w-3 h-3" data-id="element-891" /> : <ChevronDown className="w-3 h-3" data-id="element-892" />)}
                      </div>
                    </th>)}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-893">
                {filteredData.length > 0 ? filteredData.map(row => <tr key={row.id} className="hover:bg-gray-50 transition-colors" data-id="element-894">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-895">
                        {row.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-896">
                        <div className="text-sm font-medium text-gray-900" data-id="element-897">
                          {row.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-898">
                        <a href={`tel:${row.phone}`} className="hover:text-blue-600" data-id="element-899">
                          {row.phone}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-900">
                        <a href={`mailto:${row.email}`} className="hover:text-blue-600" data-id="element-901">
                          {row.email}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-902">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-903">
                          {row.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-904">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(row.status)}`} data-id="element-905">
                          {getStatusIcon(row.status)}
                          {row.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={row.message} data-id="element-906">
                        {row.message || '-'}
                      </td>
                    </tr>) : <tr data-id="element-907">
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500" data-id="element-908">
                      <div className="flex flex-col items-center justify-center" data-id="element-909">
                        <XCircle className="w-12 h-12 text-gray-300 mb-3" data-id="element-910" />
                        <p className="text-lg font-medium" data-id="element-911">לא נמצאו תוצאות</p>
                        <p className="text-sm" data-id="element-912">נסה לשנות את סינון החיפוש</p>
                      </div>
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>

          {/* Footer of table */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-913">
            <div className="text-sm text-gray-500" data-id="element-914">
              מציג <span className="font-medium" data-id="element-915">{filteredData.length}</span>{' '}
              תוצאות
            </div>
            <div className="flex gap-2" data-id="element-916">
              <button className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" disabled data-id="element-917">
                הקודם
              </button>
              <button className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" disabled data-id="element-918">
                הבא
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-919" />
    </div>;
}